import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, AlertTriangle, CheckCircle2, Printer, FileText, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, TIPOS, PEND_LABELS, SUGERIDOS, waLink, valorarMsg, fmtDate } from "@/lib/api";
import { StageBadge } from "@/components/Badges";
import { StageStepper } from "@/components/StageStepper";
import { ProcessForm } from "@/components/ProcessForm";
import { PatientTreatments } from "@/components/PatientTreatments";
import { CuestionariosPaciente } from "@/components/CuestionariosPaciente";
import { ProteccionDatosPaciente } from "@/components/ProteccionDatosPaciente";
import { CloseActions } from "@/components/CloseActions";
import { AnularCitaButton } from "@/components/AnularCita";
import { EconomicoPanel } from "@/components/EconomicoPanel";
import { ControlTerapiaPaciente } from "@/components/ControlTerapiaPaciente";
import { AgendarRevisionButton, masMeses } from "@/components/AgendarRevision";
import { useAuth, puedeEco } from "@/lib/auth";
import { UnirFicha } from "@/components/UnirFicha";
import { InformesPdf } from "@/components/InformesPdf";


export default function PacienteDetalle() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [estado, setEstado] = useState("");
  const isAdmin = puedeEco(useAuth().user);
  const esAdmin = useAuth().user?.role === "admin";
  const lastSaved = useRef(null);
  const fRef = useRef(null);
  fRef.current = f;

  const flush = useCallback(async () => {
    const cur = fRef.current;
    if (!cur) return null;
    const s = JSON.stringify(cur);
    if (s === lastSaved.current) return null;
    lastSaved.current = s;
    setEstado("guardando");
    try {
      const { data: r } = await api.put(`/patients/${id}`, cur);
      setEstado("guardado");
      if (r.created_treatments?.length) {
        toast.success(`Tratamiento iniciado: ${r.created_treatments.map((t) => TIPOS[t].single).join(", ")}`, { duration: 5000 });
      }
      return r;
    } catch (e) {
      lastSaved.current = null;
      setEstado("error");
      toast.error("No se ha podido guardar. Revisa la conexión y pulsa Guardar.");
      return null;
    }
  }, [id]);

  const load = useCallback(async () => {
    await flush();
    const r = await api.get(`/patients/${id}`);
    setData(r.data);
    lastSaved.current = JSON.stringify(r.data.patient);
    setF(r.data.patient);
  }, [id, flush]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!f || JSON.stringify(f) === lastSaved.current) return undefined;
    setEstado("pendiente");
    const t = setTimeout(async () => {
      const r = await flush();
      if (r?.created_treatments?.length) {
        load();
      } else if (r) {
        api.get(`/patients/${id}`).then((x) => setData(x.data));
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [f, flush, load, id]);
  useEffect(() => {
    const onLeave = () => {
      const cur = fRef.current;
      if (cur && JSON.stringify(cur) !== lastSaved.current) {
        lastSaved.current = JSON.stringify(cur);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patients/${id}`, {
          method: "PUT", keepalive: true, body: JSON.stringify(cur),
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      }
    };
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      flush();
    };
  }, [flush, id]);

  if (!data || !f) return <div className="text-slate-400 text-sm">Cargando…</div>;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    await flush();
    setSaving(false);
    toast.success("Cambios guardados");
    load();
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${f.nombre} y todos sus tratamientos?`)) return;
    await api.delete(`/patients/${id}`);
    toast.success("Paciente eliminado");
    nav("/pacientes");
  };

  return (
    <div data-testid="patient-detail-page">
      <Link to="/pacientes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-patients"><ArrowLeft className="h-4 w-4" />Control de pacientes</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4 fade-up">
        <div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="patient-name">{data.patient.nombre}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
            <StageBadge etapa={data.patient.etapa} testId="patient-stage" />
            <span>{data.patient.centro || "Sin centro"}</span>·<span>{data.patient.optometrista || "Sin optometrista"}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="autosave-status" className={`text-xs ${estado === "error" ? "text-rose-600" : "text-slate-500"}`}>
            {{ pendiente: "Cambios sin guardar…", guardando: "Guardando…", guardado: "Guardado automáticamente", error: "No guardado" }[estado] || ""}
          </span>
          {["evaluacion", "informe"].includes(data.patient.etapa) && f.estado_cita !== "ANULADA" && (
            <AnularCitaButton pid={id} beforeSubmit={() => api.put(`/patients/${id}`, f)} onDone={load} />
          )}
          <Button variant="outline" data-testid="presupuesto-btn" onClick={() => window.open(`/pacientes/${id}/presupuesto`, "_blank")}><FileText className="h-4 w-4 mr-1" />Presupuesto</Button>
          <Button variant="outline" data-testid="print-patient-btn" onClick={() => window.open(`/pacientes/${id}/imprimir`, "_blank")}><Printer className="h-4 w-4 mr-1" />Imprimir / PDF</Button>
          {esAdmin && <UnirFicha patient={data.patient} onDone={load} />}
          <Button variant="outline" onClick={remove} data-testid="delete-patient-btn" className="text-rose-700"><Trash2 className="h-4 w-4" /></Button>
          <Button onClick={save} disabled={saving} data-testid="save-patient-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Save className="h-4 w-4 mr-1" />{saving ? "Guardando…" : "Guardar ahora"}</Button>
        </div>
      </div>
      <div className="mb-4"><StageStepper etapa={data.patient.etapa} /></div>
      {data.pendientes.length > 0 ? (
        <div data-testid="patient-pending-banner" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <span className="text-sm font-semibold text-amber-900">Pendiente:</span>
          {data.pendientes.map((k) => <span key={k} className="rounded-full bg-white border border-amber-300 px-2.5 py-0.5 text-xs text-amber-800">{PEND_LABELS[k]}</span>)}
          {data.pendientes.includes("valorar_siguiente") && (
            <span className="text-xs text-amber-900" data-testid="valorar-aviso">Terminó Syntonic · a valorar: <b>{(data.patient.tratamientos_valorar || []).map((x) => SUGERIDOS.find((s) => s.id === x)?.label || x).join(", ")}</b>
              <button onClick={() => api.post(`/patients/${id}/valorado`).then(load)} data-testid="valorado-btn" className="ml-2 rounded border border-amber-400 bg-white px-2 py-0.5">Ya valorado</button>
              {data.patient.telefono && <a href={waLink(data.patient.telefono, valorarMsg(data.patient))} target="_blank" rel="noreferrer" onClick={() => api.post(`/patients/${id}/valorar-contactado`)} data-testid="valorar-whatsapp-btn" className="ml-2 inline-flex items-center gap-1 rounded bg-[#25D366] px-2 py-0.5 text-white"><MessageCircle className="h-3.5 w-3.5" />WhatsApp para citar</a>}</span>
          )}
        </div>
      ) : (
        <div data-testid="patient-ok-banner" className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> Sin tareas pendientes en el proceso
        </div>
      )}
      {data.patient.valorar_motivo && !data.patient.valorar_continua && (
        <div data-testid="no-sigue-ficha" className="mb-6 -mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
          Último motivo por el que no siguió tras Syntonic: <b data-testid="no-sigue-motivo">{data.patient.valorar_motivo}</b> ({fmtDate(data.patient.fecha_no_sigue)})
          {data.patient.valorar_volver && <span className="text-slate-500"> · volver a escribir el {fmtDate(data.patient.valorar_volver)}</span>}
        </div>
      )}
      {[data.patient.respuesta, data.patient.respuesta_rev].includes("NO APROBADO") && (
        <div data-testid="no-aprobado-banner" className="mb-6 -mt-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 flex flex-wrap items-center gap-2 text-sm text-sky-900">
          <span>No aprobó el tratamiento. Agenda una revisión (te propongo a los 12 meses) para recordárselo con el WhatsApp de la agenda.</span>
          <AgendarRevisionButton patient={data.patient} fecha={masMeses(data.patient.fecha_no_aprobado || new Date().toISOString().slice(0, 10), 12)} onDone={load} testId="patient-agendar-revision-btn" />
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
        <ProcessForm f={f} set={set} />
        <div className="space-y-4">
          <PatientTreatments patientId={id} treatments={data.treatments} onChange={load} />
          <InformesPdf patient={data.patient} onChange={load} />
          <ControlTerapiaPaciente patientId={id} reload={data} />
          <ProteccionDatosPaciente patientId={id} />
          <CuestionariosPaciente patientId={id} />
          {isAdmin && <EconomicoPanel f={f} set={set} treatments={data.treatments} />}
          <CloseActions patient={data.patient} alta={data.alta} onDone={load} />
        </div>
      </div>
    </div>
  );
}
